import React from 'react';
import { Collapse } from 'antd';
import InfoGrid from '../../../../components/InfoGrid';
import { displayDate } from '../../../../utils/datetime.util';
import { numberWithCommas } from '../../../../helpers/number';
import { onCalculateEstimatedMonthlyAmount } from '../../../../utils/calculation.util';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { SME_ORGANIZATION_TYPE } from '../../../../enum/smeApplication';
import { Role } from '../../../../enum/roles';
import jwt_decode from 'jwt-decode';

const { Panel } = Collapse;

export const LoanApplicationPanel = () => {
  const { smeApplicationDetails } = useSelector((state: RootState) => state.smeApplication);

  const token = localStorage.getItem('accessToken') ?? '';
  const decode: any = token ? jwt_decode(token) : null;
  const currentRole = decode?.role;

  const businessApplicationData: any =
    smeApplicationDetails?.data?.smeSolePropApplication ||
    smeApplicationDetails?.data?.smePartnershipApplication ||
    smeApplicationDetails?.data?.smeCompanyApplication;

  const applicantData =
    smeApplicationDetails?.data?.smeIndividualApplication ||
    smeApplicationDetails?.data?.smeSolePropApplication ||
    smeApplicationDetails?.data?.smePartnershipApplication ||
    smeApplicationDetails?.data?.smeStaffApplication;

  const smePartnerData = [
    {
      title: 'Partner Type',
      value: smeApplicationDetails?.data?.smePartner?.agentTypeName,
    },
    {
      title: 'Partner ID',
      value: smeApplicationDetails?.data?.redOneUsername,
    },
  ];

  const defaultLoanApplicationData = [
    {
      title: 'Application Date',
      value: displayDate(smeApplicationDetails?.data?.submissionDate),
    },
    {
      title: 'Application No.',
      value: smeApplicationDetails?.data?.applicationNo ?? '-',
    },
    {
      title: 'Campaign Code',
      value: smeApplicationDetails?.data?.promoCode ?? '-',
    },
    {
      title: 'Discount Amount',
      value: smeApplicationDetails?.data?.processingFeeToDiscount
        ? `RM ${numberWithCommas(smeApplicationDetails?.data?.processingFeeToDiscount)}`
        : '-',
    },
    {
      title:
        smeApplicationDetails?.data?.organizationType === SME_ORGANIZATION_TYPE.INDIVIDUAL ||
        smeApplicationDetails?.data?.organizationType === SME_ORGANIZATION_TYPE.STAFF
          ? 'Applicant Name'
          : 'Company Name',
      value:
        smeApplicationDetails?.data?.organizationType === SME_ORGANIZATION_TYPE.INDIVIDUAL ||
        smeApplicationDetails?.data?.organizationType === SME_ORGANIZATION_TYPE.STAFF
          ? applicantData?.name
          : businessApplicationData?.companyName,
    },
    ...(smeApplicationDetails?.data?.organizationType !== SME_ORGANIZATION_TYPE.STAFF
      ? smePartnerData
      : []),
    {
      title: 'Organization Type',
      value: smeApplicationDetails?.data?.organizationType,
    },
    {
      title: 'Applied Loan Amount',
      value: `RM ${numberWithCommas(Number(smeApplicationDetails?.data?.appliedLoanAmount))}`,
    },
    {
      title: 'Monthly Instalment',
      value: `RM ${numberWithCommas(
        onCalculateEstimatedMonthlyAmount(
          Number(smeApplicationDetails?.data?.appliedLoanAmount) || 0,
          Number(smeApplicationDetails?.data?.productLoanInterestRate) || 0,
          Number(smeApplicationDetails?.data?.productLoanTenure) || 0
        )
      )}`,
    },
    {
      title: 'Instalment Period',
      value: Number(smeApplicationDetails?.data?.productLoanTenure) || '-',
    },
    {
      title: 'Interest Rate',
      value: `${Number(smeApplicationDetails?.data?.productLoanInterestRate) || 0} %`,
    },
    {
      title: 'Loan Reason',
      value: smeApplicationDetails?.data?.loanReason,
    },
    {
      title: 'Marketing Consent',
      value: smeApplicationDetails?.data?.acceptMarketingConsent ? 'Yes' : 'No',
    },
    {
      title: 'Promo Code',
      value: smeApplicationDetails?.data?.promoCode,
    },
    {
      title: 'Current Existing Loan',
      value: 'No', // TODO: get this info from collection service once it is available
    },
  ];

  const solePropLoanApplicationData = [
    {
      title: 'SSM Expiry Date',
      value: displayDate(smeApplicationDetails?.data?.smePartner?.ssmExpiryDate),
    },
  ];

  const remainingData = [
    {
      title: 'Tax Identification Number (TIN)',
      value: smeApplicationDetails?.data?.smePartner?.tin,
    },
  ];

  const loanApplicationData = [
    ...defaultLoanApplicationData,
    ...(smeApplicationDetails?.data?.organizationType !== SME_ORGANIZATION_TYPE.INDIVIDUAL &&
    smeApplicationDetails?.data?.organizationType !== SME_ORGANIZATION_TYPE.STAFF
      ? solePropLoanApplicationData
      : []),
    ...remainingData,
  ];

  return (
    <Collapse
      ghost
      expandIconPosition="end"
      defaultActiveKey={currentRole !== Role.APPROVER_2 ? ['loanApplication'] : []}
    >
      <Panel
        key="loanApplication"
        className="application-details-section-container"
        header={<h3 className="application-details-section-title">Loan Application</h3>}
      >
        <InfoGrid data={loanApplicationData} />
      </Panel>
    </Collapse>
  );
};
